import { makeReadableDayShort, startOfDay } from '@/helpers/date';
import { isSistaminuten as isSistaminutenInstance } from '@/helpers/general';
import { getPlaceTimezone, getServiceCampaignIfAny, getServiceDuration, getServicePrice } from '@/helpers/placeHelper';
import { getUnformattedServicePrice } from '@/helpers/price';
import { _t } from '@/locale';
import { Place, PlaceCampaign, PlaceEmployee, PlaceService } from '@/types/state/shared';
import { SelectServiceItem } from './views/SelectService.types';

/**
 * The campaign period line under the service name. Ported from the place page's
 * Services row: a future campaign shows its whole span, a running one only its end.
 */
function getCampaignValidity(campaign: PlaceCampaign | undefined, place: Place): string | undefined {
  if (campaign?.startDate == null || campaign.endDate == null) return undefined;
  const timezone = getPlaceTimezone(place);
  const startDate = makeReadableDayShort(campaign.startDate * 1000, timezone);
  const endDate = makeReadableDayShort(campaign.endDate * 1000, timezone);
  return startOfDay() < campaign.startDate * 1000
    ? `${_t('campaignPrice')} ${startDate} - ${endDate}`
    : `${_t('campaignPriceUntil')} ${endDate}`;
}

/**
 * The price list every booking-flow surface prices through: the picked employee's,
 * and `0` while none is picked — the price helpers read that as "no list" and show
 * the range across all of them, rather than one employee's price for a selection
 * anyone could perform. Matches the checkout summary (`SummaryView`).
 *
 * A place's employees can sit on different lists, so a surface resolving this any
 * other way puts two prices for one selection on the same screen.
 */
export function resolvePriceListId(pickedEmployee?: PlaceEmployee): string | number {
  return pickedEmployee?.about?.priceListId ?? 0;
}

/**
 * Maps a raw PlaceService to the display shape ServiceCard renders. The price /
 * duration / campaign / wellness resolution is ported unchanged from the legacy
 * pick-service row so displayed values match SummaryView. `isRecommended` is a
 * parameter (not fixed) because add-on rows and service rows share this mapping.
 */
export function toSelectServiceItem(
  service: PlaceService,
  place: Place,
  priceListId: string | number,
  isRecommended: boolean,
): SelectServiceItem {
  // `0` is resolvePriceListId's "no list" sentinel and the price helpers detect that
  // by falsiness, so it has to stay falsy here: String(0) is '0', which
  // getServiceRangePrice would use as a substring filter over the price-list keys.
  const priceId = priceListId ? String(priceListId) : '';
  // price may be undefined (prices hidden via showPrices(), or no usable priceType) — guard everything below.
  const price = getUnformattedServicePrice(service, priceId, place);
  const { campaign, campaignService } = getServiceCampaignIfAny(service, place?.campaigns, [service.id]);

  const amounts: number[] = [];
  if (campaignService && price) {
    const offer = getUnformattedServicePrice(service, priceId, place, campaignService);
    if (offer && offer.value?.[0] != null && offer.value[0] !== price.value[0]) {
      amounts.push(price.value[0] - offer.value[0]);
    }
    if (offer && offer.type === 'range' && offer.value?.[1] != null && offer.value[1] !== price.value?.[1]) {
      amounts.push(price.value[1] - offer.value[1]);
    }
  }

  const onMainSite = !isSistaminutenInstance();
  const isSistaminuten = !onMainSite || Boolean(place?.lastMinuteDiscount);
  const priceStr = getServicePrice(service, priceListId, place, campaignService); // wellness gate only
  const acceptsWellnesscard =
    Boolean(place?.about?.settings?.acceptsGiftCard) && Boolean(service.about?.settings?.healthCareCard);
  const isWellness =
    onMainSite &&
    acceptsWellnesscard &&
    priceStr !== '' &&
    priceStr !== _t('freePrice') &&
    priceStr !== _t('variablePrice');

  return {
    id: service.id,
    name: service.name ?? '',
    duration: getServiceDuration(service, priceListId) || undefined,
    price, // undefined → ServiceCard renders no price (no price-on-request placeholder)
    discount: {
      amounts,
      percentage: isSistaminuten ? campaignService?.discountPercent : undefined,
      validity: campaignService ? getCampaignValidity(campaign, place) : undefined,
    },
    tags: { wellnessCard: isWellness, campaign: onMainSite && Boolean(campaignService) },
    description: service.about?.description ?? undefined,
    isRecommended,
  };
}
